"use client";

import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Alert, AlertDescription, Spinner } from "@akashnetwork/ui/components";
import { AddressElement, Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { StripeError } from "@stripe/stripe-js";
import { ShieldCheck } from "lucide-react";
import { useTheme } from "next-themes";
import { useServices } from "@src/context/ServicesProvider/ServicesProvider";

/** Why a payment method could not be set up, as codes only: Stripe's message is left out because it can echo what the user typed. */
export interface PaymentMethodSetupFailure {
  errorType?: string;
  errorCode?: string;
  declineCode?: string;
}
export type PaymentMethodSetupResult = {
  paymentMethodId: string;
} | {
  failure: PaymentMethodSetupFailure;
};
export type PaymentFormLoadError = "stripe_unavailable" | "load_error";

/**
 * Imperative contract exposed by a payment-method source so an orchestrator
 * (e.g. AddCreditsForm) can trigger payment-method creation without lifting
 * Stripe Elements context out of the child component.
 */
export interface PaymentMethodSourceHandle {
  /**
   * Confirms the Stripe SetupIntent and returns the new payment method id,
   * or why confirmation failed, in which case the source has already
   * surfaced the error inline.
   */
  addPaymentMethod(): Promise<PaymentMethodSetupResult>;
}
export function toPaymentMethodSetupFailure(error: StripeError): PaymentMethodSetupFailure {
  return {
    errorType: error.type,
    errorCode: error.code,
    declineCode: error.decline_code
  };
}
export const DEPENDENCIES = {
  Elements,
  useServices,
  useTheme,
  useStripe,
  useElements,
  AddressElement,
  PaymentElement
};
interface AddCreditsNewPaymentMethodFieldsProps {
  clientSecret?: string;
  isLoading: boolean;
  disabled?: boolean;
  /** Reports the payment-method type (card, bank, …) the user selects in the Stripe payment element, for analytics. */
  onPaymentTypeChange?: (type: string) => void;
  onReady?: () => void;
  onLoadFailed: (error: PaymentFormLoadError) => void;
  dependencies?: typeof DEPENDENCIES;
}
export const AddCreditsNewPaymentMethodFields = forwardRef<PaymentMethodSourceHandle, AddCreditsNewPaymentMethodFieldsProps>(function AddCreditsNewPaymentMethodFields({
  clientSecret,
  isLoading,
  disabled,
  onPaymentTypeChange,
  onReady,
  onLoadFailed,
  dependencies: d = DEPENDENCIES
}, ref) {
  const {
    stripeService
  } = d.useServices();
  const {
    resolvedTheme
  } = d.useTheme();
  const stripePromise = useMemo(() => stripeService.getStripe(), [stripeService]);
  const isDarkMode = useMemo(() => resolvedTheme === "dark", [resolvedTheme]);
  const stripeAppearance = useMemo(() => buildStripeAppearance(isDarkMode), [isDarkMode]);
  useEffect(function reportUnavailableStripe() {
    let isCurrentLoad = true;
    void stripePromise.then(stripe => {
      if (isCurrentLoad && !stripe) onLoadFailed("stripe_unavailable");
    });
    return () => {
      isCurrentLoad = false;
    };
  }, [stripePromise, onLoadFailed]);
  if (isLoading) {
    return <div className="flex items-center justify-center py-12" role="status" aria-label="Preparing payment form">
          <Spinner size="large" />
        </div>;
  }
  if (!clientSecret) {
    return null;
  }
  return <ErrorBoundary fallback={<div>Failed to load payment form</div>}>
        <d.Elements key={clientSecret} stripe={stripePromise} options={{
      clientSecret,
      appearance: stripeAppearance
    }}>
          <div ref={element => {
        if (element) element.inert = !!disabled;
      }}>
            <StripePaymentMethodFields ref={ref} dependencies={d} onPaymentTypeChange={onPaymentTypeChange} onReady={onReady} onLoadError={() => onLoadFailed("load_error")} />
          </div>
        </d.Elements>
      </ErrorBoundary>;
});
interface StripePaymentMethodFieldsProps {
  dependencies: typeof DEPENDENCIES;
  onPaymentTypeChange?: (type: string) => void;
  onReady?: () => void;
  onLoadError: () => void;
}
const StripePaymentMethodFields = forwardRef<PaymentMethodSourceHandle, StripePaymentMethodFieldsProps>(function StripePaymentMethodFields({
  dependencies: d,
  onPaymentTypeChange,
  onReady,
  onLoadError
}, ref) {
  const stripe = d.useStripe();
  const elements = d.useElements();
  const [error, setError] = useState<string | null>(null);
  useImperativeHandle(ref, () => ({
    /**
     * Confirms the SetupIntent with Stripe and returns the resulting payment
     * method id. On failure the error is rendered inline and its codes are
     * returned so the orchestrator can short-circuit and report them.
     */
    async addPaymentMethod() {
      if (!stripe || !elements) {
        setError("The payment form is still loading. Please try again in a moment.");
        return {
          failure: {
            errorType: "form_not_ready"
          }
        };
      }
      setError(null);
      let setupResult;
      try {
        setupResult = await stripe.confirmSetup({
          elements,
          redirect: "if_required"
        });
      } catch {
        setError("Couldn't save your payment method. Please check your connection and try again.");
        return {
          failure: {
            errorType: "setup_request_failed"
          }
        };
      }
      if (setupResult.error) {
        setError(setupResult.error.message || "Couldn't save your card. Please try again.");
        return {
          failure: toPaymentMethodSetupFailure(setupResult.error)
        };
      }
      const paymentMethodId = setupResult.setupIntent?.payment_method;
      if (typeof paymentMethodId !== "string") {
        setError("Couldn't save your card. Please try again.");
        return {
          failure: {
            errorType: "missing_payment_method"
          }
        };
      }
      return {
        paymentMethodId
      };
    }
  }), [stripe, elements]);
  return <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-left text-sm font-medium text-muted-foreground">BILLING ADDRESS</h3>
        <d.AddressElement options={{
        mode: "billing"
      }} />
      </div>

      <div className="space-y-2">
        <h3 className="text-left text-sm font-medium text-muted-foreground">CHOOSE A PAYMENT METHOD</h3>
        <d.PaymentElement options={{
        layout: "tabs",
        paymentMethodOrder: ["card", "link"]
      }} onChange={event => onPaymentTypeChange?.(event.value.type)} onReady={onReady} onLoadError={onLoadError} />
      </div>

      <Alert variant="default" className="bg-muted p-3">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <AlertDescription className="text-muted-foreground">
          Your payment is securely processed by Stripe. You will be charged when you purchase credits.
        </AlertDescription>
      </Alert>

      {error && <Alert variant="destructive" aria-label="Payment error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>}
    </div>;
});

/**
 * Builds the Stripe Elements appearance config so it stays out of the JSX and
 * recomputes only when the resolved theme flips between light and dark.
 */
function buildStripeAppearance(isDarkMode: boolean) {
  return {
    theme: (isDarkMode ? "night" : "stripe") as "night" | "stripe",
    variables: {
      colorPrimary: isDarkMode ? "#fff" : "#000",
      colorSuccess: "#16a34a",
      borderRadius: "8px",
      fontWeightNormal: "500",
      colorBackground: isDarkMode ? "#262626" : "#fff"
    },
    rules: {
      ".Input": {
        padding: "8px",
        boxShadow: "none"
      },
      ".Tab": {
        boxShadow: "none"
      },
      ".PickerItem": {
        boxShadow: "none"
      },
      ...(isDarkMode && {
        ".Tab--selected": {
          border: "1px solid #fff",
          backgroundColor: "#262626"
        },
        ".TabIcon--selected": {
          fill: "#fff"
        },
        ".TabLabel--selected": {
          color: "#fff"
        }
      })
    }
  };
}